import React, { useState, useEffect, forwardRef } from 'react';
import { Col, Row, Statistic, Table, Skeleton, Tag } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { recibosEncabezadoPreNomina } from '../../../../apis/ProcesoPreNominaApi';
import { status_recibo_prenomina } from '../../../../constants/reciboPrenomina';


const Result = forwardRef(function Result({ header, refreshKey }, ref) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bitacoraInfo, setBitacoraInfo] = useState(null);
  const [totales, setTotales] = useState({
    diasTrabajados: 0,
    horasTrabajadas: 0,
    horasExtras: 0,
    horasExtrasDobles: 0,
    horasExtrasTriples: 0,
    descanso: 0,
    festivos: 0,
    faltas: 0,
  });

  useEffect(() => {
    (async () => {
      if (header?.num_periodo > 0) {
        await refreshData();
      }
    })();
  }, [header, refreshKey]);

  const refreshData = async () => {
    setLoading(true);
    try {
      const response = await recibosEncabezadoPreNomina(header.periodo_id, header.anio, header.num_periodo);
      const items = response.ok ? response.payload || [] : [];
      setData(items);

      const sumField = (field) => items.reduce((total, item) => total + (Number(item?.[field]) || 0), 0);
      setTotales({
        diasTrabajados: sumField('dias_trabajados'),
        horasTrabajadas: sumField('horas_trabajadas'),
        horasExtras: sumField('horas_extras'),
        horasExtrasDobles: sumField('horas_extras_dobles'),
        horasExtrasTriples: sumField('horas_extras_triples'),
        descanso: sumField('descanso'),
        festivos: sumField('festivos'),
        faltas: sumField('faltas'),
      });

      let itemsErrorTotal = 0;
      let itemsProcesadoTotal = 0;
      items.forEach((item) => {
        if (!item.success) {
          itemsErrorTotal++;
        }
        if (item.success) {
          itemsProcesadoTotal++;
        }
      });

      setBitacoraInfo({
        usuarios_procesados: itemsProcesadoTotal + itemsErrorTotal,
        usuarios_procesados_exito: itemsProcesadoTotal,
        usuarios_procesados_error: itemsErrorTotal,
        usuarios_esperados: items.length,
      });
    } finally {
      setLoading(false);
    }
  };

  const renderEstatus = (item) => {
    if (item === status_recibo_prenomina.CERRADO) {
      return <Tag color={'#108ee9'}>Cerrada</Tag>;
    }
    if (item === status_recibo_prenomina.PROCESADO) {
      return <Tag color={'#87d068'}>Procesado</Tag>;
    }
    if (item === status_recibo_prenomina.EN_PROCESO) {
      return <Tag color={'#2db7f5'}>En Proceso</Tag>;
    }
    if (item === status_recibo_prenomina.ERROR) {
      return <Tag color={'#f5222d'}>Error</Tag>;
    }
    if (item === status_recibo_prenomina.NUEVO) {
      return <Tag color={'#faad14'}>Nuevo</Tag>;
    }
    return <Tag>Nuevo</Tag>;
  };

  const columns = [
    {
      title: 'Empleado',
      dataIndex: 'empleado',
      key: 'empleado',
    },
    {
      title: 'Etapa',
      dataIndex: 'estatus',
      key: 'estatus',
      render: renderEstatus,
    },
    {
      title: 'Estado',
      dataIndex: 'success',
      key: 'success',
      render: (item) => {
        if (item) {
          return <CheckOutlined style={{ color: 'green' }} />;
        }
        return <CloseOutlined style={{ color: 'red' }} />;
      },
    },
  ];

  return (
    <>
      <Row style={{ paddingTop: 10 }}>
        <Col md={{ span: 6 }} sm={{ span: 12 }} xs={{ span: 24 }}>
          Días Trabajados <br /><b>{totales.diasTrabajados.toFixed(2)}</b>
        </Col>
        <Col md={{ span: 6 }} sm={{ span: 12 }} xs={{ span: 24 }}>
          Horas Trabajadas <br /><b>{totales.horasTrabajadas.toFixed(2)}</b>
        </Col>
        <Col md={{ span: 6 }} sm={{ span: 12 }} xs={{ span: 24 }}>
          Horas Extras <br /><b>{totales.horasExtras.toFixed(2)}</b>
        </Col>
        <Col md={{ span: 6 }} sm={{ span: 12 }} xs={{ span: 24 }}>
          Faltas <br /><b>{totales.faltas.toFixed(2)}</b>
        </Col>
      </Row>
      <Row style={{ paddingTop: 10 }}>
        <Col md={{ span: 6 }} sm={{ span: 12 }} xs={{ span: 24 }}>
          Extras Dobles <br /><b>{totales.horasExtrasDobles.toFixed(2)}</b>
        </Col>
        <Col md={{ span: 6 }} sm={{ span: 12 }} xs={{ span: 24 }}>
          Extras Triples <br /><b>{totales.horasExtrasTriples.toFixed(2)}</b>
        </Col>
        <Col md={{ span: 6 }} sm={{ span: 12 }} xs={{ span: 24 }}>
          Descanso Trabajado <br /><b>{totales.descanso.toFixed(2)}</b>
        </Col>
        <Col md={{ span: 6 }} sm={{ span: 12 }} xs={{ span: 24 }}>
          Festivo Trabajado <br /><b>{totales.festivos.toFixed(2)}</b>
        </Col>
      </Row>
      {bitacoraInfo && (
        <Row gutter={16} style={{ paddingTop: 10, paddingBottom: 10 }}>
          <Col span={7} offset={1}>
            <Statistic title="Procesados" value={bitacoraInfo.usuarios_procesados} suffix={`/ ${bitacoraInfo.usuarios_esperados}`} />
          </Col>
          <Col span={7}>
            <Statistic title="Correctos" value={bitacoraInfo.usuarios_procesados_exito} suffix={`/ ${bitacoraInfo.usuarios_esperados}`} prefix={<CheckOutlined style={{ color: 'green' }} />} />
          </Col>
          <Col span={7}>
            <Statistic title="Incorrectos" value={bitacoraInfo.usuarios_procesados_error} suffix={`/ ${bitacoraInfo.usuarios_esperados}`} prefix={<CloseOutlined style={{ color: 'red' }} />} />
          </Col>
        </Row>
      )}
      {loading ? (
        <Skeleton active={loading} />
      ) : (
        <>
          {data.length == 0 ? <></> : <Table dataSource={data} columns={columns} rowKey={(row) => row.id || row.odoo_empleado_id} />}
        </>
      )}
    </>
  );
});

export default Result;
